import { useState, useRef, useEffect, useCallback } from 'react';
import { useAutoSave } from '@/hooks';
import { useConfirmDialog } from '../../../hooks/useConfirmDialog';
import { ConfirmDialog } from '../../shared/UIComponents';
import {
  ArrowPathIcon,
  TrashIcon,
  Cog6ToothIcon,
  EllipsisVerticalIcon,
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import disciplineConfig from '../../../services/disciplineConfigService';
import EditableField from '../../shared/EditableField';
import MultiSelectDropdown from '../../shared/MultiSelectDropdown';
import { useSchool } from '@/contexts/SchoolContext';
import { GOVERNED_RULE_TIMEFRAMES } from '@/services/domain/domainRuleValidation';
import { useRuleFormSnapshotSync } from './useRuleFormSnapshotSync';

// Helper function to get singular/plural form for options
const getPluralLabel = (options, value, optionValue) => {
  const option = options.find(opt => opt.value === optionValue);
  if (!option) return optionValue;
  if (value > 1) {
    return option.plural || option.label;
  }
  return option.singular || option.label;
};

const EscalationRuleForm = ({
  rule = null,
  ruleId = null,
  categoryKey = null,
  onSave,
  onDelete,
  isRecognitionMode = false,
  domainActionDefinitionOptions = null,
  domainLabel = null,
  repairMode = false,
}) => {
  const { confirmDialog, showConfirm } = useConfirmDialog();
  const { schoolId } = useSchool();
  const isGovernedDomain = Array.isArray(domainActionDefinitionOptions);
  const firstDomainAction = domainActionDefinitionOptions?.[0];
  const nextDomainAction = domainActionDefinitionOptions?.[1] || firstDomainAction;

  // Initialize with proper defaults
  const [data, setData] = useState(() => {
    const defaults = {
      title: domainLabel ? `${domainLabel} escalation` : (isRecognitionMode ? 'Upgrade Rule' : 'Escalation Rule'),
      groups: [],
      threshold: 2,
      monitorsType: firstDomainAction?.value || 'friday-detention',
      monitorsTypeId: firstDomainAction?.id || null,
      timeCount: 1,
      timeframe: 'year',
      escalatesTo: nextDomainAction?.value || 'saturday-detention',
      escalatesToId: nextDomainAction?.id || null,
      resetAfterUse: true,
      active: true
    };

    if (!rule) return defaults;

    return {
      title: rule.title || rule.name || defaults.title,
      groups: rule.groups || defaults.groups,
      threshold: rule.threshold || defaults.threshold,
      monitorsType: rule.monitorsType || defaults.monitorsType,
      monitorsTypeId: rule.monitorsTypeId || null,
      timeCount: rule.timeCount || defaults.timeCount,
      timeframe: rule.timeframe || defaults.timeframe,
      escalatesTo: rule.escalatesTo || defaults.escalatesTo,
      escalatesToId: rule.escalatesToId || null,
      resetAfterUse: rule.resetAfterUse !== undefined ? rule.resetAfterUse : defaults.resetAfterUse,
      active: rule.active !== undefined ? rule.active : defaults.active,
      ...(rule.id && { id: rule.id })
    };
  });
  const { markLocalChanges, reconcileIncomingData } = useRuleFormSnapshotSync(data, setData);

  const [showConfig, setShowConfig] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [consequenceTypeOptions, setConsequenceTypeOptions] = useState([]);
  const [timeframeOptions, setTimeframeOptions] = useState([]);
  const [gradeOptions, setGradeOptions] = useState([]);

  useEffect(() => {
    if (repairMode) setShowConfig(true);
  }, [repairMode]);

  const menuRef = useRef();

  const { save: debouncedSave } = useAutoSave(onSave, 100);

  const update = useCallback((updates) => {
    // If updating monitorsType or escalatesTo, also update the corresponding ID field
    if (updates.monitorsType) {
      const typeOption = consequenceTypeOptions.find(opt => opt.value === updates.monitorsType);
      if (typeOption) {
        updates.monitorsTypeId = typeOption.id;
      }
    }
    if (updates.escalatesTo) {
      const typeOption = consequenceTypeOptions.find(opt => opt.value === updates.escalatesTo);
      if (typeOption) {
        updates.escalatesToId = typeOption.id;
      }
    }

    markLocalChanges(updates);
    const newData = { ...data, ...updates };
    setData(newData);

    const actualRuleId = ruleId || rule?.id || newData.id;
    debouncedSave(newData, categoryKey || 'escalation', actualRuleId);
  }, [data, debouncedSave, categoryKey, ruleId, rule?.id, consequenceTypeOptions, markLocalChanges]);

  // Governed Domains supply their own stable Action ids. Behaviour and
  // Recognition retain the established consequence/reward service.
  useEffect(() => {
    if (isGovernedDomain) {
      setConsequenceTypeOptions(domainActionDefinitionOptions);
      return undefined;
    }
    const loadConsequenceTypes = async () => {
      try {
        const types = await disciplineConfig.getConsequenceTypes(schoolId);
        setConsequenceTypeOptions(types.map(c => ({
          value: c.value,
          label: c.label,
          singular: c.singular,
          plural: c.plural,
          id: c.id  // Include the Firestore document ID
        })));
      } catch (error) {
        console.error('Error loading consequence types:', error);
      }
    };
    loadConsequenceTypes();
  }, [schoolId, domainActionDefinitionOptions, isGovernedDomain]);

  // Backfill missing typeIds after consequence types load
  useEffect(() => {
    if (consequenceTypeOptions.length === 0) return;

    const needsUpdate = {};

    // Check if monitorsTypeId is missing but monitorsType exists
    if (data.monitorsType && !data.monitorsTypeId) {
      const typeOption = consequenceTypeOptions.find(opt => opt.value === data.monitorsType);
      if (typeOption) {
        needsUpdate.monitorsTypeId = typeOption.id;
      }
    }

    // Check if escalatesToId is missing but escalatesTo exists
    if (data.escalatesTo && !data.escalatesToId) {
      const typeOption = consequenceTypeOptions.find(opt => opt.value === data.escalatesTo);
      if (typeOption) {
        needsUpdate.escalatesToId = typeOption.id;
      }
    }

    // If we found missing IDs, update them
    if (Object.keys(needsUpdate).length > 0) {
      update(needsUpdate);
    }
  }, [consequenceTypeOptions, data.monitorsType, data.monitorsTypeId, data.escalatesTo, data.escalatesToId, update]);

  // Load timeframes from Firestore
  useEffect(() => {
    if (isGovernedDomain) {
      setTimeframeOptions(GOVERNED_RULE_TIMEFRAMES);
      return undefined;
    }
    const loadTimeframes = async () => {
      try {
        const options = await disciplineConfig.getTimeframeOptions(schoolId);
        setTimeframeOptions(options);
      } catch (error) {
        console.error('Error loading timeframes:', error);
      }
    };
    loadTimeframes();
  }, [schoolId, isGovernedDomain]);

  // Load grades from Firestore
  useEffect(() => {
    const loadGrades = async () => {
      try {
        const options = await disciplineConfig.getGradeSelectOptions(schoolId);
        setGradeOptions(options);
      } catch (error) {
        console.error('Error loading grades:', error);
      }
    };
    loadGrades();
  }, [schoolId]);

  // Reconcile every live snapshot, including updates to this same rule id.
  useEffect(() => {
    if (!rule?.id) return;
    reconcileIncomingData({
      title: rule.title || rule.name || (domainLabel ? `${domainLabel} escalation` : (isRecognitionMode ? 'Upgrade Rule' : 'Escalation Rule')),
      groups: rule.groups || [],
      threshold: rule.threshold || 2,
      monitorsType: rule.monitorsType || firstDomainAction?.value || 'friday-detention',
      monitorsTypeId: rule.monitorsTypeId || firstDomainAction?.id || null,
      timeCount: rule.timeCount || 1,
      timeframe: rule.timeframe || 'year',
      escalatesTo: rule.escalatesTo || nextDomainAction?.value || 'saturday-detention',
      escalatesToId: rule.escalatesToId || nextDomainAction?.id || null,
      resetAfterUse: rule.resetAfterUse !== undefined ? rule.resetAfterUse : true,
      active: rule.active !== undefined ? rule.active : true,
      id: rule.id,
    });
  }, [rule, domainLabel, firstDomainAction?.id, firstDomainAction?.value, isRecognitionMode, nextDomainAction?.id, nextDomainAction?.value, reconcileIncomingData]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sortable setup
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rule?.id || 'temp' });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  const getWhoText = () => {
    return data.groups.length > 0 ? data.groups.join(', ') : 'any student';
  };

  // Helper to get the proper label for a consequence type
  const getConsequenceLabel = (value, count = 1) => {
    const option = consequenceTypeOptions.find(opt => opt.value === value);
    if (!option) return value;
    return count > 1 ? option.plural : option.singular;
  };

  // Helper to get the reset description based on checkbox state
  const getResetDescription = () => {
    const monitorsLabel = getConsequenceLabel(data.monitorsType, data.threshold);
    const escalatesToLabel = getConsequenceLabel(data.escalatesTo, 2); // plural for "become"

    if (data.resetAfterUse) {
      // Ticked: Can trigger multiple times
      return `The learner must receive ${data.threshold} new ${monitorsLabel} to ${isRecognitionMode ? 'upgrade' : 'escalate'} again`;
    } else {
      // Unticked: Permanent escalation
      return `All future ${monitorsLabel} permanently become ${escalatesToLabel}`;
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded-lg shadow-sm ${!isDragging ? 'hover:shadow-md' : ''} relative transition-shadow`}
    >
      <div className="flex items-center p-4">
        {/* Drag Handle */}
        <div
          className="mr-2 py-1 cursor-grab hover:bg-base-200 rounded focus:outline-none"
          {...attributes}
          {...listeners}
          tabIndex={-1}
        >
          <svg
            className="w-4 h-4 text-base-content/50"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <circle cx="7" cy="5" r="1.5" />
            <circle cx="7" cy="10" r="1.5" />
            <circle cx="7" cy="15" r="1.5" />
            <circle cx="13" cy="5" r="1.5" />
            <circle cx="13" cy="10" r="1.5" />
            <circle cx="13" cy="15" r="1.5" />
          </svg>
        </div>

        <div className="flex-1">
          {/* Editable Title */}
          <div className="mb-3">
            <input
              type="text"
              defaultValue={data.title}
              onBlur={(e) => {
                const newTitle = e.target.value.trim() || (domainLabel ? `${domainLabel} escalation` : (isRecognitionMode ? 'Upgrade Rule' : 'Escalation Rule'));
                if (newTitle !== data.title) {
                  update({ title: newTitle });
                }
                setEditingTitle(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.target.blur();
                }
              }}
              className={`text-lg font-medium bg-transparent border-0 p-0 w-full outline-none cursor-pointer ${
                editingTitle ? 'text-black' : 'text-blue-600 hover:text-blue-700'
              }`}
              onFocus={() => setEditingTitle(true)}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="text-base pr-4">
              After <span className="font-medium text-blue-600">{getWhoText()}</span> {isGovernedDomain ? 'receives' : (isRecognitionMode ? 'earns' : 'gets')}{' '}
              <EditableField
                field="threshold"
                value={data.threshold}
                type="number"
                onUpdate={update}
                editingField={editingField}
                setEditingField={setEditingField}
                validation={{ min: 1, max: 999 }}
              />
              {' '}
              <EditableField
                field="monitorsType"
                value={data.monitorsType}
                options={consequenceTypeOptions.map(opt => ({
                  value: opt.value,
                  label: getPluralLabel(consequenceTypeOptions, data.threshold, opt.value)
                }))}
                onUpdate={update}
                editingField={editingField}
                setEditingField={setEditingField}
              />
              {' '}in{' '}
              <EditableField
                field="timeCount"
                value={data.timeCount}
                type="number"
                onUpdate={update}
                editingField={editingField}
                setEditingField={setEditingField}
                validation={{ min: 1, max: 999 }}
              />
              {' '}
              <EditableField
                field="timeframe"
                value={data.timeframe}
                options={timeframeOptions.map(opt => ({
                  value: opt.value,
                  label: getPluralLabel(timeframeOptions, data.timeCount, opt.value)
                }))}
                onUpdate={update}
                editingField={editingField}
                setEditingField={setEditingField}
              />
              , the next {getConsequenceLabel(data.monitorsType)} becomes a <EditableField
                field="escalatesTo"
                value={data.escalatesTo}
                options={consequenceTypeOptions.map(opt => ({
                  value: opt.value,
                  label: opt.singular // Always singular since it's "the next X becomes a Y"
                }))}
                onUpdate={update}
                editingField={editingField}
                setEditingField={setEditingField}
              />
              {data.resetAfterUse && (
                <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-xs text-purple-700 whitespace-nowrap">
                  <ArrowPathIcon aria-hidden="true" className="h-3.5 w-3.5" />
                  Resets after use
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              {/* Active toggle */}
              <input
                type="checkbox"
                checked={data.active}
                onChange={(e) => update({ active: e.target.checked })}
                className={`toggle toggle-sm ${data.active ? 'toggle-success' : ''}`}
              />

              {/* Three dots menu */}
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="p-1.5 hover:bg-base-200 rounded"
                >
                  <EllipsisVerticalIcon className="w-4 h-4" />
                </button>

                <AnimatePresence>
                  {showMenu && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: -10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -10 }}
                      className="absolute right-0 mt-1 w-40 bg-white border border-base-200 rounded-lg shadow-lg z-10 overflow-hidden"
                    >
                      <button
                        onClick={() => {
                          setShowConfig(!showConfig);
                          setShowMenu(false);
                        }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-base-100 flex items-center gap-2"
                      >
                        <Cog6ToothIcon className="w-4 h-4" />
                        {showConfig ? 'Less options' : 'More options'}
                      </button>
                      {onDelete && (
                        <button
                          onClick={async () => {
                            setShowMenu(false);
                            const confirmed = await showConfirm({
                              title: 'Delete Rule?',
                              message: 'Delete this rule permanently? This action cannot be undone.',
                              variant: 'danger',
                              confirmText: 'Delete',
                              cancelText: 'Cancel'
                            });
                            if (confirmed) {
                              const deleteId = ruleId || rule?.id || data.id;
                              if (deleteId) {
                                await onDelete(deleteId);
                              }
                            }
                          }}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-error/10 text-error flex items-center gap-2"
                        >
                          <TrashIcon className="w-4 h-4" />
                          Delete
                        </button>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>

          <AnimatePresence mode="wait">
            {showConfig && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.15, ease: "easeInOut" }}
                style={{ overflow: 'hidden' }}
              >
                <div className="mt-4 pt-4 border-t border-base-200 space-y-4">
                  <MultiSelectDropdown
                    label="TARGET GROUPS"
                    value={data.groups}
                    options={gradeOptions}
                    onChange={(newGroups) => update({ groups: newGroups })}
                    placeholder="All grades"
                  />

                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id={`reset-${rule?.id || 'new'}`}
                      checked={data.resetAfterUse}
                      onChange={(e) => update({ resetAfterUse: e.target.checked })}
                      className="checkbox checkbox-sm checkbox-primary"
                    />
                    <label
                      htmlFor={`reset-${rule?.id || 'new'}`}
                      className="text-sm cursor-pointer select-none"
                    >
                      <span className="font-medium">Reset after each {isRecognitionMode ? 'upgrade' : 'escalation'}</span>
                      <span className="text-base-content/60 ml-2">
                        — {getResetDescription()}
                      </span>
                    </label>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog {...confirmDialog} />
    </div>
  );
};

export default EscalationRuleForm;
